import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { SmartbouffeLogoSmall } from "../components/Logo";
import { Footer } from "../components/Footer";
import AdBanner from '../components/AdBanner';
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";
import { 
  PeanutIcon, TreeNutIcon, ShellfishIcon, FishIcon, EggIcon, DairyIcon, WheatIcon, WarningIcon,
  VegetarianIcon, VeganIcon, CarnivoreIcon, KetoIcon, PaleoIcon, LowCarbIcon, HalalIcon, KosherIcon,
  SoyIcon, SesameIcon, SulfitesIcon, PersonIcon, KeyIcon, SaveIcon
} from '../components/Icons';

const DIETARY_OPTIONS = [
  { id: "vegetarian", label: "Végétarien", icon: <VegetarianIcon size={16} /> },
  { id: "vegan", label: "Vegan", icon: <VeganIcon size={16} /> },
  { id: "pescatarian", label: "Pescatarien", icon: <FishIcon size={16} /> },
  { id: "carnivore", label: "Carnivore", icon: <CarnivoreIcon size={16} /> },
  { id: "gluten-free", label: "Sans gluten", icon: <WheatIcon size={16} /> },
  { id: "lactose-free", label: "Sans lactose", icon: <DairyIcon size={16} /> },
  { id: "keto", label: "Cétogène", icon: <KetoIcon size={16} /> },
  { id: "paleo", label: "Paléo", icon: <PaleoIcon size={16} /> },
  { id: "low-carb", label: "Faible en glucides", icon: <LowCarbIcon size={16} /> },
  { id: "halal", label: "Halal", icon: <HalalIcon size={16} /> },
  { id: "kosher", label: "Casher", icon: <KosherIcon size={16} /> },
];

const ALLERGY_OPTIONS = [
  { id: "peanuts", label: "Arachides", icon: <PeanutIcon size={16} /> },
  { id: "tree-nuts", label: "Noix (amandes, noix, cajou)", icon: <TreeNutIcon size={16} /> },
  { id: "shellfish", label: "Fruits de mer (crevettes, crabe)", icon: <ShellfishIcon size={16} /> },
  { id: "fish", label: "Poisson", icon: <FishIcon size={16} /> },
  { id: "eggs", label: "Œufs", icon: <EggIcon size={16} /> },
  { id: "dairy", label: "Produits laitiers", icon: <DairyIcon size={16} /> },
  { id: "soy", label: "Soja", icon: <SoyIcon size={16} /> },
  { id: "wheat", label: "Blé", icon: <WheatIcon size={16} /> },
  { id: "sesame", label: "Sésame", icon: <SesameIcon size={16} /> },
  { id: "sulfites", label: "Sulfites", icon: <SulfitesIcon size={16} /> },
];

export default function Profile() {
  const [profile, setProfile] = useState(null);
  const [selectedDiets, setSelectedDiets] = useState([]);
  const [selectedAllergies, setSelectedAllergies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // États pour modification profil
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  
  // État pour code promo
  const [promoCode, setPromoCode] = useState("");
  const [applyingPromo, setApplyingPromo] = useState(false);
  
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await axios.get(`${API}/user/profile`, { withCredentials: true });
      setProfile(response.data);
      setSelectedDiets(response.data.dietary_preferences || []);
      setSelectedAllergies(response.data.allergies || []);
      setName(response.data.name || "");
      setEmail(response.data.email || "");
    } catch (error) {
      toast.error("Erreur lors du chargement du profil");
    } finally {
      setLoading(false);
    }
  };

  const toggleDiet = (dietId) => {
    setSelectedDiets(prev => {
      const newValue = prev.includes(dietId)
        ? prev.filter(d => d !== dietId)
        : [...prev, dietId];
      return newValue;
    });
  };

  const toggleAllergy = (allergyId) => {
    setSelectedAllergies(prev =>
      prev.includes(allergyId)
        ? prev.filter(a => a !== allergyId)
        : [...prev, allergyId]
    );
  };

  const handleSaveInfo = async () => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/user/profile`,
        {
          name,
          email,
          dietary_preferences: selectedDiets,
          allergies: selectedAllergies
        },
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      // Mettre à jour le contexte utilisateur
      setUser({...user, name, email});
      toast.success("Profil mis à jour!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API}/user/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword
        },
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      toast.success("Mot de passe mis à jour!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowPasswordSection(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du changement de mot de passe");
    } finally {
      setSaving(false);
    }
  };

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) {
      toast.error("Veuillez entrer un code promo");
      return;
    }
    
    setApplyingPromo(true);
    try {
      const response = await axios.post(
        `${API}/promo-codes/apply`,
        { code: promoCode.toUpperCase() },
        { withCredentials: true }
      );
      
      toast.success(`${response.data.message} Retournez au tableau de bord pour voir vos crédits mis à jour.`);
      setPromoCode("");
      loadProfile(); // Recharger le profil pour voir les nouveaux crédits
    } catch (error) {
      toast.error(error.response?.data?.detail || "Code promo invalide");
    } finally {
      setApplyingPromo(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <header className="dashboard-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <SmartbouffeLogoSmall />
          <h2>Mon Profil</h2>
        </div>
        <Button variant="ghost" onClick={() => navigate("/dashboard")}>
          <ArrowLeft size={20} className="mr-2" />
          Retour
        </Button>
      </header>

      {/* Admin Button - Only visible for admins */}
      {profile?.is_admin && (
        <div style={{ 
          maxWidth: '800px', 
          margin: '0 auto 1rem', 
          padding: '0 1rem'
        }}>
          <Button 
            onClick={() => navigate("/admin")}
            style={{ 
              width: '100%', 
              background: 'linear-gradient(135deg, #87A96B 0%, #6B8A54 100%)',
              color: 'white',
              padding: '1rem',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            🛠️ Dashboard Administrateur
          </Button>
        </div>
      )}

      <div className="profile-container">
        <div className="profile-card">
          <div className="profile-header">
            <h1>Mon Profil</h1>
            <p>Gérez vos informations personnelles et préférences alimentaires</p>
          </div>

          {/* Section Informations Personnelles */}
          <div className="profile-section" style={{ marginBottom: '2rem' }}>
            <Label className="section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <PersonIcon size={20} color="#5D6F61" /> Informations Personnelles
            </Label>
            
            <div style={{ marginTop: '1rem' }}>
              <Label htmlFor="name">Nom</Label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Votre nom"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  marginTop: '0.5rem',
                  borderRadius: '8px',
                  border: '1px solid #E8E4D9',
                  fontSize: '1rem'
                }}
              />
            </div>

            <div style={{ marginTop: '1rem' }}>
              <Label htmlFor="email">Email</Label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  marginTop: '0.5rem',
                  borderRadius: '8px',
                  border: '1px solid #E8E4D9',
                  fontSize: '1rem'
                }}
              />
            </div>

            {/* Section Mot de passe */}
            <div style={{ marginTop: '1.5rem' }}>
              <Button
                variant="outline"
                onClick={() => setShowPasswordSection(!showPasswordSection)}
                style={{ marginBottom: '1rem' }}
              >
                {showPasswordSection ? "Annuler changement de mot de passe" : "🔒 Changer mon mot de passe"}
              </Button>

              {showPasswordSection && (
                <div style={{ padding: '1rem', background: '#F5F1E8', borderRadius: '8px', marginTop: '0.5rem' }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <Label htmlFor="current-password">Mot de passe actuel</Label>
                    <input
                      id="current-password"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Votre mot de passe actuel"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        marginTop: '0.5rem',
                        borderRadius: '8px',
                        border: '1px solid #E8E4D9',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <Label htmlFor="new-password">Nouveau mot de passe</Label>
                    <input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Minimum 6 caractères"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        marginTop: '0.5rem',
                        borderRadius: '8px',
                        border: '1px solid #E8E4D9',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
                    <input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Retapez le nouveau mot de passe"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        marginTop: '0.5rem',
                        borderRadius: '8px',
                        border: '1px solid #E8E4D9',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <Button
                    onClick={handleChangePassword}
                    disabled={saving || !currentPassword || !newPassword || !confirmPassword}
                    className="save-button"
                  >
                    <Save size={20} className="mr-2" />
                    {saving ? "Mise à jour..." : "Mettre à jour le mot de passe"}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Bouton Sauvegarder en haut */}
          <div className="profile-actions" style={{ marginBottom: '1.5rem', borderBottom: '2px solid #E8E4D9', paddingBottom: '1rem' }}>
            <Button
              onClick={handleSaveInfo}
              disabled={saving}
              className="save-button"
            >
              <Save size={20} className="mr-2" />
              {saving ? "Sauvegarde..." : "Sauvegarder mes informations"}
            </Button>
          </div>

          {/* Section Code Promo */}
          <div className="profile-section" style={{ marginBottom: '2rem', padding: '1.5rem', background: 'linear-gradient(135deg, #F0F7EC 0%, #E8F5E1 100%)', borderRadius: '12px', border: '2px solid #87A96B' }}>
            <Label className="section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#3A4A3E', marginBottom: '1rem' }}>
              🎁 Code Promo
            </Label>
            <p style={{ fontSize: '0.9rem', color: '#5D6F61', marginBottom: '1rem' }}>
              Vous avez un code promo ? Entrez-le ici pour recevoir des crédits ou un rabais !
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder="Entrez votre code"
                style={{
                  flex: '1',
                  minWidth: '200px',
                  padding: '0.75rem 1rem',
                  borderRadius: '8px',
                  border: '2px solid #87A96B',
                  fontSize: '1rem',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleApplyPromoCode()}
              />
              <Button
                onClick={handleApplyPromoCode}
                disabled={applyingPromo || !promoCode.trim()}
                style={{
                  background: '#87A96B',
                  color: 'white',
                  padding: '0.75rem 1.5rem',
                  fontWeight: '600'
                }}
              >
                {applyingPromo ? "Application..." : "Appliquer"}
              </Button>
            </div>
          </div>

          <div className="profile-section">
            <Label className="section-title">🍽️ Régimes Alimentaires</Label>
            <p className="section-description">
              Sélectionnez vos régimes alimentaires. L'IA vous proposera des alternatives compatibles.
            </p>
            <div className="options-grid">
              {DIETARY_OPTIONS.map(option => (
                <button
                  key={option.id}
                  onClick={() => toggleDiet(option.id)}
                  className={`option-card ${selectedDiets.includes(option.id) ? 'selected' : ''}`}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ display: 'inline-flex' }}>{option.icon}</span>
                    <span className="option-label">{option.label}</span>
                  </span>
                  {selectedDiets.includes(option.id) && <span className="check-mark">✓</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="profile-section">
            <Label className="section-title">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                <WarningIcon size={20} style={{ color: '#FFA726' }} />
                Allergies et Intolérances
              </span>
            </Label>
            <p className="section-description">
              Indiquez vos allergies. Les recettes incompatibles seront signalées avec des alternatives.
            </p>
            <div className="options-grid">
              {ALLERGY_OPTIONS.map(option => (
                <button
                  key={option.id}
                  onClick={() => toggleAllergy(option.id)}
                  className={`option-card ${selectedAllergies.includes(option.id) ? 'selected' : ''}`}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ display: 'inline-flex' }}>{option.icon}</span>
                    <span className="option-label">{option.label}</span>
                  </span>
                  {selectedAllergies.includes(option.id) && <span className="check-mark">✓</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Bouton Sauvegarder en bas */}
          <div className="profile-actions" style={{ marginTop: '1.5rem', borderTop: '2px solid #E8E4D9', paddingTop: '1rem' }}>
            <Button
              onClick={handleSaveInfo}
              disabled={saving}
              className="save-button"
            >
              <Save size={20} className="mr-2" />
              {saving ? "Sauvegarde..." : "Sauvegarder mes informations"}
            </Button>
          </div>

          {/* Bouton Retour en bas de la page */}
          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <Button 
              variant="outline" 
              onClick={() => navigate("/dashboard")}
              style={{
                padding: '0.75rem 2rem',
                fontSize: '1rem'
              }}
            >
              <ArrowLeft size={20} className="mr-2" />
              Retour au tableau de bord
            </Button>
          </div>
        </div>
      </div>
      
      <AdBanner />
      <Footer />
    </div>
  );
}
